import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Navigation from './Navigation';
import OptimizedImage from './OptimizedImage';
import { Helmet } from 'react-helmet-async';
import { useIsMobile } from '@/hooks/use-mobile';
import { Bell } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/utils';

const Header = () => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const logoSrc = "/lovable-uploads/cd25ed71-78b3-45d5-baec-37bdcc2ad1a4.png";
  const location = useLocation();
  const isMobile = useIsMobile();
  const isHomePage = location.pathname === '/';
  const { user } = useAuth();
  
  // Determine if route is active
  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path);
  };
  
  // Handle scroll event to adjust header appearance
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  // Preload the image immediately on component mount
  useEffect(() => {
    const preloadImage = new Image();
    preloadImage.onload = () => {
      setImageLoaded(true);
    };
    preloadImage.onerror = (e) => {
      console.error('Failed to preload logo image:', e);
    };
    
    // Start loading with high priority
    preloadImage.src = logoSrc;
  }, []);

  // Determine header appearance based on page and scroll position
  const headerClasses = `fixed top-0 left-0 right-0 w-full z-50 transition-all duration-200 ${
    isScrolled
      ? 'bg-background/95 backdrop-blur-sm border-b shadow-sm'
      : isHomePage && isMobile
        ? 'bg-transparent'
        : 'bg-background/80 backdrop-blur-sm border-b'
  }`;

  // Adjust logo size based on scroll position and device
  const logoSize = isMobile
    ? isScrolled ? 'w-[30px] h-[30px]' : 'w-[36px] h-[36px]'
    : isScrolled ? 'w-[70px] h-[70px]' : 'w-[90px] h-[90px]';

  // Adjust padding based on scroll position and device
  const headerPadding = isMobile
    ? isScrolled ? 'py-2' : 'py-2'
    : isScrolled ? 'py-1' : 'py-3';

  // Instagram-style mobile header
  if (isMobile) {
    return (
      <>
        <Helmet>
          <link rel="preload" href={logoSrc} as="image" />
        </Helmet>
        <header className={headerClasses}>
          <div className="container px-4" style={{ paddingTop: 0, paddingBottom: 0 }}>
            <div className={`flex items-center justify-between ${headerPadding}`}>
              <Link to="/" className="flex items-center">
                <div className={`${logoSize} flex items-center justify-center transition-all duration-200`}>
                  {!imageLoaded && (
                    <div className="w-full h-full bg-gray-100 rounded-full flex items-center justify-center">
                      <span className="text-xs text-gray-400">PIF</span>
                    </div>
                  )}
                  <OptimizedImage 
                    src={logoSrc}
                    alt="PIF Logo"
                    width={36}
                    height={36}
                    priority={true}
                    objectFit="contain"
                    className={`w-full h-full transition-opacity duration-300 ${
                      imageLoaded ? 'opacity-100' : 'opacity-0 absolute'
                    }`}
                    onLoad={() => setImageLoaded(true)}
                    onError={(e) => {
                      console.error('Image load error in component', e);
                      setImageLoaded(true);
                    }}
                  />
                </div>
                <h1 className="sr-only">Populism Immunity Framework</h1>
              </Link>
              
              <div className="flex items-center gap-3">
                {user && (
                  <Button 
                    variant="gradient" 
                    size="icon-circle" 
                    gradientFrom="rgb(139, 92, 246)" 
                    gradientTo="rgb(217, 70, 239)"
                    className={cn(
                      "transition-all duration-200",
                      isActive('/pif-insight/topics') ? "shadow-md" : "opacity-80 bg-gradient-to-br from-purple-100 to-purple-200"
                    )}
                    asChild
                  >
                    <Link to="/pif-insight/topics">
                      <Bell className={cn(
                        "h-5 w-5 transition-transform",
                        isActive('/pif-insight/topics') ? "text-white" : "text-purple-500"
                      )} />
                    </Link>
                  </Button>
                )}
                
                {/* Search icon button removed */}
              </div>
            </div>
          </div>
        </header>
      </>
    );
  }

  // Return original desktop header
  return (
    <>
      <Helmet>
        <link rel="preload" href={logoSrc} as="image" />
      </Helmet>
      <header className={headerClasses}>
        <div className="container max-w-5xl mx-auto px-4 sm:px-6" style={{ paddingTop: 0, paddingBottom: 0 }}>
          <div className={`flex items-center justify-between ${headerPadding}`}>
            <Link to="/" className="flex items-center gap-1.5 sm:gap-2">
              <div className={`${logoSize} flex items-center justify-center transition-all duration-200`}>
                {/* Display a lightweight placeholder while image loads */}
                {!imageLoaded && (
                  <div className="w-full h-full bg-gray-100 rounded-full flex items-center justify-center">
                    <span className="text-xs text-gray-400">PIF</span>
                  </div>
                )}
                {/* Optimized image with width and height to prevent layout shift */}
                <OptimizedImage 
                  src={logoSrc}
                  alt="PIF Logo"
                  width={90}
                  height={90}
                  priority={true}
                  objectFit="contain"
                  className={`w-full h-full transition-opacity duration-300 ${
                    imageLoaded ? 'opacity-100' : 'opacity-0 absolute'
                  }`}
                  onLoad={() => setImageLoaded(true)}
                  onError={(e) => {
                    console.error('Image load error in component', e);
                    // Still set as loaded to show at least something
                    setImageLoaded(true);
                  }}
                />
              </div>
              {/* Hidden for visual purposes but kept for SEO - hidden on all screen sizes */}
              <h1 className="sr-only">
                Populism Immunity Framework
              </h1>
              <span className="sr-only">
                Populism Immunity Framework - Strengthening civic resilience
              </span>
            </Link>
            <Navigation />
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
